import React, { Component, Fragment } from "react";
import { Redirect, NavLink } from "react-router-dom";
import moment from "moment";
import {
  Grid,
  Col,
  Row,
  Tabs,
  Tab,
  Button,
  OverlayTrigger,
  Tooltip,
  Panel,
  Badge
} from "react-bootstrap";
import debounce from "lodash/debounce";
import snakeCase from "lodash/snakeCase";
import remove from "lodash/remove";
import unionBy from "lodash/unionBy";
import { Link } from "react-router-dom";

import { get, put, download } from "../../../utils/DeApi";
import {
  publish,
  subscribe,
  unsubscribe,
  presence
} from "../../../utils/Broadcast";

import "./Scene.css";
import NoteList from "../NoteList/NoteList";
import SceneEdit from "../SceneEdit/SceneEdit";
import SceneDelete from "../SceneDelete/SceneDelete";
import SceneCreate from "../SceneCreate/SceneCreate";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";
import Loader from "../../Loader/Loader";
import ErrorHandler from "../../ErrorHandler/ErrorHandler";
import ReAuth from "../../App/ReAuth/ReAuth";
import RewardDetector from "../ProjectRewards/RewardDetector/RewardDetector";
import PageVisibility from "../../App/PageVisibility/PageVisibility";
import { FocusModeContext } from "../FocusModeContext/FocusModeContext";
/**
 * Handles rendering of the writing mode for a scene.
 */
class Scene extends Component {
  constructor(props) {
    super(props);
    this.subscribedPromises = [];
    this.state = {
      reAuthenticate: false,
      syncing: false,
      isMobile: /mobi/i.test(navigator.userAgent.toLowerCase()),
      connections: [],

      matchCase: true,
      wholeWord: true,
      activePillarSelections: [],
      wordMatch: [],
      sceneMatchCount: 0
    };

    this.handleSceneCreated = this._handleSceneCreated.bind(this);
    this.handleSceneDeleted = this._handleSceneDeleted.bind(this);
    this.handleReauthenticated = this._handleReauthenticated.bind(this);
    this.handleSceneUpdated = this._handleSceneUpdated.bind(this);
    this.handleSceneExport = this._handleSceneExport.bind(this);
    this.handleRichTextUpdated = this._handleRichTextUpdated.bind(this);
    this.handleSceneEditing = this._handleSceneEditing.bind(this);
    this.findReplaceStatus = this._findReplaceStatus.bind(this);

    this.handleVisibilityChange = debounce(
      visibilty => {
        this._handleVisibilityChange(visibilty);
      },
      60000,
      {
        leading: true,
        trailing: false
      }
    );

    this.handleSceneBodyUpdated = debounce(
      (body, success, error) => {
        this.updateSceneBody(body, success, error);
      },
      1000,
      {
        maxWait: 10000
      }
    );

    this.handleSceneStatsUpdated = debounce((stats, success, error) => {
      this.updateSceneStats(stats, success, error);
    }, 1000);

    this.handleSearchWordMatch = debounce((params, success, error) => {
      this.searchWordMatch(params, success, error);
    }, 300);
  }

  _handleSceneDeleted() {
    this.setState({ deleted: true });
  }

  _handleSceneCreated(scene) {
    this.setState(function(prevState, props) {
      prevState.scenes.splice(parseInt(scene.insertIndex), 0, scene);
      return {
        scenes: prevState.scenes
      };
    });
  }

  _handleSceneUpdated(scene) {
    this.setState(function(prevState, props) {
      return {
        scenes: prevState.scenes.map(item => {
          if (item.id === scene.id) return scene;
          else return item;
        }),
        scene: scene
      };
    });
  }

  _handleRichTextUpdated(body, successCallback, errorCallback) {
    const { scene, connections } = this.state;
    const channel = `Scene.${scene.id}`;

    if (connections.length > 1) {
      publish(channel, "typing", { status: true });
      this.handleSceneBodyUpdated(
        body,
        response => {
          publish(channel, "typing", { status: false });
          successCallback(response);
        },
        error => {
          publish(channel, "typing", { status: false });
          errorCallback(error);
        }
      );
    } else {
      this.handleSceneBodyUpdated(body, successCallback, errorCallback);
    }
  }

  _handleReauthenticated() {
    this.setState({ reAuthenticate: false });
  }

  _handleSceneExport() {
    const { project } = this.props;
    const { scene } = this.state;

    let exportPromise = download(`/manuscript/${project.id}/scenes`, {
      params: {
        scenes: [scene.id]
      }
    });

    const fileName = snakeCase(scene.title) + ".docx";

    exportPromise.promise
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute("download", fileName);
        document.body.appendChild(link);

        link.click();
        // success
      })
      .catch(error => {
        // error
      });
    this.subscribedPromises.push(exportPromise);
  }

  _handleSceneEditing(editing) {
    this.setState({ editing: editing });
  }

  _handleVisibilityChange(visibilty) {
    const { editing, scene } = this.state;

    if (
      moment().diff(moment.utc(scene.updatedAt), "minutes") > 30 &&
      visibilty &&
      !editing
    ) {
      this.setState({ scene: "", scenes: [] });
      this.fetchScene();
      this.fetchScenes();
      this.fetchSceneTypes();
    }
  }

  _findReplaceStatus(status) {
    this.setState({
      activePillarSelections: status.activePillarSelections,
      sceneMatchCount: status.activePillarSelections.length > 0 ? 1 : 0,
      isOpen: status.isOpen,
      wordMatch: []
    });

    if (status.isOpen && status.find && status.find.length > 0) {
      this.handleSearchWordMatch({
        query: status.find,
        matchCase: status.matchCase,
        wholeWord: status.wholeWord,
        pillar: "scene"
      });
    }
  }

  componentDidMount() {
    this.fetchScene();
    this.fetchScenes();
    this.fetchSceneTypes();
    this.registerBroadcasting();
  }

  componentDidUpdate(prevProps) {
    if (this.props.sceneId !== prevProps.sceneId) {
      this.setState({
        scene: "",
        scenes: [],
        error: ""
      });
      this.fetchScene();
      this.fetchScenes();
      this.fetchSceneTypes();
    }
  }

  componentWillUnmount() {
    this.subscribedPromises.forEach(function(promise) {
      promise.cancel();
    });
    this.handleSceneBodyUpdated.cancel();
    unsubscribe(`Scene.${this.props.sceneId}`);
  }

  registerBroadcasting() {
    const channel = `Scene.${this.props.sceneId}`;
    subscribe(channel, "typing", event => {
      if (event.status) {
        this.setState({ syncing: true });
      } else {
        this.setState({ scene: "", syncing: false });
        this.fetchScene();
      }
    });
    presence(channel)
      .here(connections => {
        this.setState({ connections: connections || [] });
      })
      .joining(connection => {
        this.setState((state, props) => {
          return {
            connections: unionBy(state.connections, [connection], "name") || []
          };
        });
      })
      .leaving(connection => {
        this.setState((state, props) => {
          return {
            connections: remove(state.connections, item => {
              return connection.name !== item.name;
            })
          };
        });
      });
  }

  fetchScene() {
    let scenePromise = get("/scenes/" + this.props.sceneId);
    scenePromise.promise
      .then(response => {
        this.setState({
          scene: response.data,
          error: ""
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            error: error
          });
      });
    this.subscribedPromises.push(scenePromise);
  }

  fetchScenes() {
    let scenesPromise = get("/scenes?project=" + this.props.project.id);
    scenesPromise.promise
      .then(response => {
        this.setState({
          scenes: response.data,
          error: ""
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            error: error
          });
      });
    this.subscribedPromises.push(scenesPromise);
  }

  fetchSceneTypes() {
    let sceneTypesPromise = get("/scene-types");
    this.setState({ isLoading: true });
    sceneTypesPromise.promise
      .then(response => {
        this.setState({
          sceneTypes: response.data,
          isLoading: false
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            error: error,
            isLoading: false
          });
      });
    this.subscribedPromises.push(sceneTypesPromise);
  }

  searchWordMatch = params => {
    this.setState({ wordMatchLoading: true });
    let scenesPromise = get(
      `/projects/${this.props.project.id}/pillar-search`,
      {
        params: params
      }
    );
    scenesPromise.promise
      .then(response => {
        const wordMatch = response.data;
        let count = this.state.sceneMatchCount;
        wordMatch.forEach(item => {
          if (item.match > 0 && item.sceneId !== this.props.sceneId) count++;
        });

        this.setState({
          wordMatch: wordMatch,
          sceneMatchCount: count,
          wordMatchLoading: false,
          matchError: ""
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            wordMatchLoading: false,
            matchError: error
          });
      });

    this.subscribedPromises.push(scenesPromise);
  };

  updateSceneBody(body, successCallback, errorCallback) {
    const { scene } = this.state;
    let updateBodyPromise = put("/scenes/" + scene.id + "/body", {
      body: JSON.stringify(body)
    });

    this.setState({ reAuthenticate: false });

    updateBodyPromise.promise
      .then(response => {
        this.setState(
          {
            reAuthenticate: false,
            scene: response.data
          },
          () => {
            successCallback(response);
          }
        );
      })
      .catch(error => {
        if (!error.isCanceled) {
          if (error.status === 401) this.setState({ reAuthenticate: true });
          errorCallback(error);
        }
      });

    this.subscribedPromises.push(updateBodyPromise);
  }

  updateSceneStats(stats, successCallback, errorCallback) {
    if (stats) {
      let updateStatsPromise = put(
        "/scenes/" + this.state.scene.id + "/stats",
        stats
      );
      this.setState({ reAuthenticate: false });
      updateStatsPromise.promise
        .then(response => {
          this.setState({ sceneStats: response.data });
          successCallback(response);
        })
        .catch(error => {
          if (!error.isCanceled) {
            if (error.status === 401) this.setState({ reAuthenticate: true });
            errorCallback(error);
          }
        });
      this.subscribedPromises.push(updateStatsPromise);
    }
  }

  render() {
    const {
      scene,
      scenes,
      sceneTypes,
      sceneStats,
      deleted,
      error,
      syncing,
      reAuthenticate,
      isMobile,
      isOpen,
      sceneMatchCount,
      wordMatchLoading
    } = this.state;
    const { project } = this.props;

    if (deleted) return <Redirect to={"/projects/" + project.id + "/scenes"} />;
    if (error) return <ErrorHandler error={error} />;
    if (!scene || !scenes) return <Loader />;

    let previous = scenes[parseInt(scene.orderId) - 2];
    let next = scenes[parseInt(scene.orderId)];

    return (
      <FocusModeContext.Consumer>
        {({ isFocused }) => (
          <Grid fluid className="PillarView">
            <Row className="PillarCanvas">
              <Panel
                id="collapsible-panel-example-2"
                expanded={this.state.toggle}
                onToggle={() => {}}
                className="mb-0 hidden-md hidden-lg"
              >
                <Panel.Heading>
                  <div className="panel-title">
                    <Row>
                      <Col xs={2}>
                        {previous ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip">
                                Go to previous scene
                              </Tooltip>
                            }
                          >
                            <Link
                              className="pull-left text-left"
                              style={{ width: 40 }}
                              to={`/projects/${project.id}/scenes/${previous.id}`}
                            >
                              <i className="material-icons md-24">
                                arrow_backward
                              </i>
                            </Link>
                          </OverlayTrigger>
                        ) : (
                          <span />
                        )}
                      </Col>
                      <Col
                        xs={8}
                        onClick={() =>
                          this.setState({
                            toggle: !this.state.toggle
                          })
                        }
                      >
                        <h4 className="pt-0 pb-0 mb-0 text-center">
                          {scene.title}
                        </h4>
                      </Col>
                      <Col xs={2}>
                        {next ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip">Go to next scene</Tooltip>
                            }
                          >
                            <Link
                              className="pull-right text-right"
                              style={{ width: 40 }}
                              to={`/projects/${project.id}/scenes/${next.id}`}
                            >
                              <i className="material-icons md-24">
                                arrow_forward
                              </i>
                            </Link>
                          </OverlayTrigger>
                        ) : (
                          <span />
                        )}
                      </Col>
                    </Row>
                  </div>
                </Panel.Heading>
                <Panel.Collapse>
                  <Panel.Body>
                    <p className="text-right">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-edit">
                            Edit title and summary
                          </Tooltip>
                        }
                      >
                        <span>
                          <SceneEdit
                            scene={scene}
                            sceneTypes={sceneTypes}
                            onSceneUpdated={this.handleSceneUpdated}
                            onSceneEditing={this.handleSceneEditing}
                          />
                        </span>
                      </OverlayTrigger>
                    </p>
                    {scene.description ? (
                      <p className="PillarDescription mt-xs mb-sm">
                        {scene.description}
                      </p>
                    ) : (
                      ""
                    )}
                    <ul className="list-inline">
                      <li>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-export">
                              Export this scene
                            </Tooltip>
                          }
                        >
                          <Button
                            className="btn-secondary"
                            bsSize="xs"
                            onClick={this.handleSceneExport}
                          >
                            Export
                          </Button>
                        </OverlayTrigger>
                      </li>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-delete">
                            Delete this scene
                          </Tooltip>
                        }
                      >
                        <li>
                          <SceneDelete
                            scene={scene}
                            onSceneDeleted={this.handleSceneDeleted}
                          />
                        </li>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-new">Create a new scene</Tooltip>
                        }
                      >
                        <li>
                          <SceneCreate
                            project={project}
                            scenes={scenes || []}
                            onSceneCreated={this.handleSceneCreated}
                            label="New"
                            className="btn-secondary btn-xs"
                          />
                        </li>
                      </OverlayTrigger>
                    </ul>
                  </Panel.Body>
                </Panel.Collapse>
              </Panel>
              <Col xs={12} md={3} className="PillarLeft hidden-xs hidden-sm">
                <div className={`mt-md mb-md ${isFocused ? "hidden" : ""}`}>
                  <h4 className="pt-xs pb-xs">{scene.title}</h4>

                  <Tabs defaultActiveKey={1} id="scene-meta-data-tabs">
                    <Tab eventKey={1} title="Summary">
                      <p className="text-right">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-edit">
                              Edit title and summary
                            </Tooltip>
                          }
                        >
                          <span>
                            <SceneEdit
                              scene={scene}
                              sceneTypes={sceneTypes}
                              onSceneUpdated={this.handleSceneUpdated}
                              onSceneEditing={this.handleSceneEditing}
                            />
                          </span>
                        </OverlayTrigger>
                      </p>
                      {scene.description ? (
                        <p className="PillarDescription mt-xs mb-sm">
                          {scene.description}
                        </p>
                      ) : (
                        <p className="sub-text mt-xs mb-sm">...</p>
                      )}
                    </Tab>
                    <Tab
                      eventKey={2}
                      title={
                        <span>
                          All Scenes{" "}
                          {isOpen && (
                            <Fragment>
                              {wordMatchLoading ? (
                                <div className="find-loader"></div>
                              ) : (
                                <Fragment>
                                  {sceneMatchCount > 0 && (
                                    <Badge>{sceneMatchCount}</Badge>
                                  )}
                                </Fragment>
                              )}
                            </Fragment>
                          )}
                        </span>
                      }
                    >
                      {this.renderScenes()}
                    </Tab>
                  </Tabs>

                  <div className="mt-sm mb-sm">
                    <ul className="list-inline">
                      <li>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-export">
                              Export this scene
                            </Tooltip>
                          }
                        >
                          <Button
                            className="btn-secondary"
                            bsSize="xs"
                            onClick={this.handleSceneExport}
                          >
                            Export
                          </Button>
                        </OverlayTrigger>
                      </li>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-delete">
                            Delete this scene
                          </Tooltip>
                        }
                      >
                        <li>
                          <SceneDelete
                            scene={scene}
                            onSceneDeleted={this.handleSceneDeleted}
                          />
                        </li>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-new">Create a new scene</Tooltip>
                        }
                      >
                        <li>
                          <SceneCreate
                            project={project}
                            scenes={scenes || []}
                            onSceneCreated={this.handleSceneCreated}
                            label="New"
                            className="btn-secondary btn-xs"
                          />
                        </li>
                      </OverlayTrigger>
                    </ul>
                    <ul className="list-inline">
                      <li>
                        {previous ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip">
                                Go to previous scene
                              </Tooltip>
                            }
                          >
                            <Button
                              className="btn-secondary"
                              bsSize="xs"
                              componentClass={Link}
                              to={`/projects/${project.id}/scenes/${previous.id}`}
                            >
                              Previous
                            </Button>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            disabled
                            bsSize="xs"
                            className="btn-secondary"
                          >
                            Previous
                          </Button>
                        )}
                      </li>
                      <li>
                        {next ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip">Go to next scene</Tooltip>
                            }
                          >
                            <Button
                              className="btn-secondary"
                              bsSize="xs"
                              componentClass={Link}
                              to={`/projects/${project.id}/scenes/${next.id}`}
                            >
                              Next
                            </Button>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            disabled
                            bsSize="xs"
                            className="btn-secondary"
                          >
                            Next
                          </Button>
                        )}
                      </li>
                    </ul>
                  </div>

                  <div className="FooterStickyLeft">
                    <ul className="list-inline text-right">
                      <li>
                        <RewardDetector
                          project={project}
                          sceneStats={sceneStats}
                        />
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6} className="PillarCenter">
                <div className={!isMobile ? "mt-md mb-xl" : ""}>
                  <Row className="PillarEditor">
                    <Col xs={12}>
                      <RichTextEditor
                        richText={scene.body}
                        controls={!isMobile}
                        counter={true}
                        focusMode={true}
                        findReplaceStatus={this.findReplaceStatus}
                        onRichTextUpdated={this.handleRichTextUpdated}
                        onStatsUpdated={this.handleSceneStatsUpdated}
                        syncing={syncing}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col xs={12} md={3} className="PillarRight">
                <div className={`mt-md mb-xl ${isFocused ? "hidden" : ""}`}>
                  <NoteList pillarId={scene.id} pillarType="scene" />
                </div>
              </Col>
              {!!reAuthenticate && (
                <ReAuth onReauthenticated={this.handleReauthenticated} />
              )}
              <PageVisibility
                onVisibilityChange={this.handleVisibilityChange}
              />
            </Row>
          </Grid>
        )}
      </FocusModeContext.Consumer>
    );
  }

  renderScenes() {
    const { project } = this.props;
    const { scenes, activePillarSelections, isOpen, wordMatch } = this.state;
    return (
      <div className="list-group numbered-list-group">
        {scenes.map((scene, index) => {
          return (
            <NavLink
              key={scene.id}
              to={"/projects/" + project.id + "/scenes/" + scene.id}
              activeClassName="active"
              className="list-group-item"
            >
              {(isOpen && wordMatch.length > 0) ||
              (activePillarSelections &&
                isOpen &&
                activePillarSelections.length > 0) ? (
                <Fragment>
                  {scene.title && scene.title.length > 28
                    ? scene.title.slice(0, 25) + "..."
                    : scene.title}
                </Fragment>
              ) : (
                scene.title
              )}

              {this.props.sceneId === scene.id ? (
                <Fragment>
                  {activePillarSelections &&
                    isOpen &&
                    activePillarSelections.length > 0 && (
                      <Badge className="pull-right">
                        {activePillarSelections.length}
                      </Badge>
                    )}
                </Fragment>
              ) : (
                <Fragment>
                  {wordMatch &&
                    isOpen &&
                    wordMatch.length > 0 &&
                    wordMatch.map((item, index) => {
                      if (item.sceneId === scene.id && item.match > 0)
                        return (
                          <Badge key={index} className="pull-right">
                            {item.match}
                          </Badge>
                        );
                      else return <Fragment key={index} />;
                    })}
                </Fragment>
              )}
            </NavLink>
          );
        })}
      </div>
    );
  }
}

export default Scene;
